import React, { useEffect, useState } from "react";
import { Provider, useDispatch } from "react-redux";
import ReactSelect from "./components/ReactSelect";
import toast, { Toaster } from "react-hot-toast";
import { validateEmail } from "../../../utils/validation";
import { AdminServices } from "../../../services/admin/AdminServices";
import RegisterScreen from "./components/RegisterScreen";
import RegisterUser from "./components/RegisterUser";
import UserInfo from "./components/UserInfo";
import { store, AppDispatch } from "./store";
import {
  setSearchedUserDetails,
  setSelectedOrgId,
  setSelectedStoreId,
} from "./store/userSlice";
import StoreInfo from "./components/StoreInfo";

// Inner component that uses Redux
const ControlHubInner = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [userIdOrEmail, setUserIdOrEmail] = useState("");
  const [isUserSearched, setIsUserSearched] = useState(false);
  const [isUserSearchLoading, setIsUserSearchLoading] = useState(false);
  const [accessInfoList, setAccessInfoList] = useState<any[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<any | null>(null);
  const [selectedStore, setSelectedStore] = useState<any | null>(null);
  const [activeAction, setActiveAction] = useState<string>("");

  const Actions = [
    { action: "Get User Information" },
    { action: "Get Store Information" },
    { action: "Get Screen Information" },
    { action: "Register User" },
    { action: "Register Screen" },
  ];

  const handleUserSearch = async () => {
    setIsUserSearched(false);
    setAccessInfoList([]);
    setSelectedOrg(null);
    setSelectedStore(null);
    setActiveAction("");
    if (!userIdOrEmail) return toast.error("Enter User id or email");

    setIsUserSearchLoading(true);
    try {
      const isEmail = validateEmail(userIdOrEmail);
      const { response } = await AdminServices.getUserInfo({
        userIdOrEmail,
        isEmail,
      });

      if (response && response.accessInfo) {
        // Store full response in Redux
        dispatch(setSearchedUserDetails(response));

        const modifiedAccessInfoList = Object.entries(response.accessInfo).map(
          ([orgId, data]: [string, any]) => ({
            orgId,
            organizationName: data.organizationName,
            stores: data.stores,
          })
        );
        toast.success("User fetched");
        setAccessInfoList(modifiedAccessInfoList);
        setSelectedOrg(modifiedAccessInfoList[0]);
        setIsUserSearched(true);
      }
    } catch (error) {
      toast.error("Error while fetching user");
    } finally {
      setIsUserSearchLoading(false);
    }
  };

  const renderActionComponent = () => {
    switch (activeAction) {
      case "Get User Information":
        return (
          userIdOrEmail &&
          selectedOrg && <UserInfo userIdOrEmail={userIdOrEmail} />
        );
      case "Get Store Information":
        return <StoreInfo/>
      case "Register User":
        return <RegisterUser />;
      case "Register Screen":
        return <RegisterScreen />;
      default:
        return (
          <div className="text-gray-500 h-[10vh] text-semibold text-xl flex justify-center items-center">
            Select an action above
          </div>
        );
    }
  };

useEffect(() => {
  return () => {
    dispatch(setSearchedUserDetails(null));
    dispatch(setSelectedOrgId(null));
    dispatch(setSelectedStoreId(null));
  };
}, [dispatch]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      <div className="m-6 flex flex-col gap-6 bg-white rounded-xl ">
        <div className="heading bg-blue-800 px-6 h-[10vh] flex items-center rounded-tl-xl rounded-tr-xl">
          <h2 className="text-2xl font-bold text-white">Control Hub</h2>
        </div>
        <div className="box px-6 flex flex-col gap-6 mb-6">
          {/* Search Input */}
          <div className="relative flex items-center">
            <input
              className="w-full px-4 py-4 border border-neutral-300 rounded-xl text-md text-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              type="text"
              placeholder="Search by email or User ID"
              value={userIdOrEmail}
              onChange={(e) => {
                setUserIdOrEmail(e.target.value);
                setActiveAction("");
                setIsUserSearched(false);
              }}
            />
            <button
              disabled={!userIdOrEmail}
              className={`absolute right-3 text-white px-4 py-2 rounded-xl ${
                !userIdOrEmail
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-800 hover:bg-blue-900"
              }`}
              onClick={handleUserSearch}
            >
              Search
            </button>
          </div>

          {isUserSearchLoading && (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-12 h-12 border-4 border-dashed rounded-full animate-spin mb-4"></div>
              <span className="text-blue-700 font-medium">
                Fetching user info...
              </span>
            </div>
          )}

          {isUserSearched && (
            <div className="flex flex-col gap-6">
              {/* Org and Store Select */}
              <div className="flex gap-6">
                <div className="flex flex-col gap-3 flex-1">
                  <label>Select Organization</label>
                  <ReactSelect
                    options={accessInfoList.map((org) => ({
                      value: org.orgId,
                      label: org.organizationName,
                    }))}
                    value={
                      selectedOrg
                        ? {
                            value: selectedOrg.orgId,
                            label: selectedOrg.organizationName,
                          }
                        : null
                    }
                    onChange={(option) => {
                      const org = accessInfoList.find(
                        (o) => o.orgId === option?.value
                      );
                      setSelectedOrg(org || null);
                      setSelectedStore(null);

                      // ✅ store in Redux
                      console.log("Dispatching OrgId:", option?.value);
                      dispatch(
                        setSelectedOrgId(
                          option?.value ? String(option.value) : null
                        )
                      );
                      dispatch(setSelectedStoreId(null)); // reset store selection
                    }}
                  />
                </div>

                <div className="flex flex-col gap-3 flex-1">
                  <label>Select Store</label>
                  <ReactSelect
                    options={
                      selectedOrg
                        ? selectedOrg.stores.map((store: any) => ({
                            value: store.storeId,
                            label: store.storeName,
                            role: store.role,
                          }))
                        : []
                    }
                    value={
                      selectedStore
                        ? {
                            value: selectedStore.storeId,
                            label: selectedStore.storeName,
                          }
                        : null
                    }
                    onChange={(option) => {
                      const store = selectedOrg?.stores.find(
                        (s: any) => s.storeId === option?.value
                      );
                      setSelectedStore(store || null);

                      // ✅ store in Redux
                      console.log("Dispatching store id:", option?.value);
                      dispatch(
                        setSelectedStoreId(
                          option?.value ? String(option.value) : null
                        )
                      );
                    }}
                  />
                </div>
              </div>

              {/* Action Tabs */}
              <div className="actions flex flex-col gap-3 ">
                <h2>Actions</h2>
                <div className="Action-tabs flex flex-wrap gap-6">
                  {Actions.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => setActiveAction(item.action)}
                      className={`px-4 py-2 rounded-lg w-1/5 flex justify-center cursor-pointer ${
                        activeAction === item.action
                          ? "bg-blue-900 text-white"
                          : "bg-blue-800 text-white hover:bg-blue-900"
                      }`}
                    >
                      {item.action}
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Area */}
              <div className="action-area px-4">{renderActionComponent()}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Outer component with Redux Provider
const ControlHub: React.FC = () => {
  return (
    <Provider store={store}>
      <ControlHubInner />
    </Provider>
  );
};

export default ControlHub;
